import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { CollectionList } from 'types/interfaces/collectionList';
import { CardCondition } from 'types/interfaces/condition/condition';
import { useGetCardPrintingsQuery } from 'store/services/cards/search/cardsSearchApi';
import { UpdateHaveListCardRequest } from 'store/services/collectionList/haveList/haveListApi';
import { FoilCardImage } from 'components/FoilCardImage';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { decimalToPoints } from 'utils/decimalUtils';
import './EditHaveListCardModal.css';

interface Props {
  isOpen: boolean;
  card: CollectionList | null;
  onClose: () => void;
  onSave: (updates: UpdateHaveListCardRequest) => Promise<void>;
}

interface PrintingOption {
  id: number;
  setCode: string;
  setName: string;
  collectorNumber: string;
  lang: string;
  releasedAt: string;
  scryfallID: string;
  pointsNonFoil: number | null;
  pointsFoil: number | null;
}

const CONDITION_OPTIONS = [
  { value: CardCondition.NEAR_MINT, label: 'Near Mint (NM)' },
  { value: CardCondition.SLIGHTLY_PLAYED, label: 'Lightly Played (LP)' },
  { value: CardCondition.MODERATELY_PLAYED, label: 'Moderately Played (MP)' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'ja', label: 'Japanese' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ko', label: 'Korean' },
  { value: 'ru', label: 'Russian' },
  { value: 'zhs', label: 'Chinese Simplified' },
];

const EditHaveListCardModal: React.FC<Props> = ({ isOpen, card, onClose, onSave }) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedPrintingId, setSelectedPrintingId] = useState<number | null>(null);
  const [selectedFoil, setSelectedFoil] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState<CardCondition>(CardCondition.NEAR_MINT);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedForTrade, setSelectedForTrade] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Lazy-load printings when modal opens
  // Handle both scryfallId (backend) and scryfallID (TypeScript interface) casings
  const scryfallId = (card?.card as any)?.scryfallId || card?.card?.scryfallID || '';
  const { data: printings, isLoading: isPrintingsLoading } = useGetCardPrintingsQuery(
    scryfallId,
    { skip: !isOpen || !scryfallId }
  );

  // Initialize form with current card values
  useEffect(() => {
    if (card && isOpen) {
      setQuantity(card.quantity || 1);
      setSelectedPrintingId(card.card?.id || null);
      setSelectedFoil(card.foil || false); // Use UserMTGCardHave.foil, not card printing's foil
      setSelectedCondition(card.condition || CardCondition.NEAR_MINT);
      setSelectedLanguage(card.card?.lang || 'en');
      setSelectedForTrade(card.forTrade !== false);
      setSearchTerm('');
    }
  }, [card, isOpen]);

  // Build individual printing options filtered by selected language
  const printingOptions = useMemo((): PrintingOption[] => {
    if (!printings) return [];

    return (printings as any[])
      .filter((p: any) => (p.lang || 'en') === selectedLanguage)
      .map((p: any) => {
        const priceData = Array.isArray(p.price) ? p.price[0] : p.price;
        return {
          id: p.id,
          setCode: p.setCode || '',
          setName: p.setName || p.setCode || '',
          collectorNumber: p.collectorNumber || '',
          lang: p.lang || 'en',
          releasedAt: p.releasedAt || '',
          scryfallID: p.scryfallID || (p as any).scryfallId || '',
          pointsNonFoil: priceData?.usd != null ? decimalToPoints(priceData.usd) : null,
          pointsFoil: priceData?.usdFoil != null ? decimalToPoints(priceData.usdFoil) : null,
        };
      })
      .sort((a, b) => {
        // Primary: release date descending (newest first)
        if (a.releasedAt && b.releasedAt) {
          const dateDiff = new Date(b.releasedAt).getTime() - new Date(a.releasedAt).getTime();
          if (dateDiff !== 0) return dateDiff;
        }
        // Secondary: set name alphabetical
        const nameDiff = a.setName.localeCompare(b.setName);
        if (nameDiff !== 0) return nameDiff;
        // Tertiary: collector number (natural sort)
        return a.collectorNumber.localeCompare(b.collectorNumber, undefined, { numeric: true });
      });
  }, [printings, selectedLanguage]);

  // Filter printings by search term
  const filteredPrintings = useMemo(() => {
    if (!searchTerm) return printingOptions;

    const lower = searchTerm.toLowerCase();
    return printingOptions.filter(p =>
      p.setName.toLowerCase().includes(lower) ||
      p.setCode.toLowerCase().includes(lower) ||
      p.collectorNumber.toLowerCase().includes(lower)
    );
  }, [printingOptions, searchTerm]);

  // Get available languages from all printings (not filtered by set)
  const availableLanguages = useMemo(() => {
    if (!printings) return [];

    const langs = new Set<string>();
    (printings as any[]).forEach((p: any) => {
      langs.add(p.lang || 'en');
    });

    return LANGUAGE_OPTIONS.filter(opt => langs.has(opt.value));
  }, [printings]);

  // Check foil availability for the selected printing
  const foilAvailability = useMemo(() => {
    if (!selectedPrintingId) {
      return { foil: false, nonFoil: false };
    }
    const selected = printingOptions.find(p => p.id === selectedPrintingId);
    if (!selected) {
      return { foil: false, nonFoil: false };
    }
    return {
      foil: selected.pointsFoil !== null,
      nonFoil: selected.pointsNonFoil !== null,
    };
  }, [printingOptions, selectedPrintingId]);

  // Auto-correct foil when selected printing doesn't support it
  useEffect(() => {
    if (selectedFoil && !foilAvailability.foil && foilAvailability.nonFoil) {
      setSelectedFoil(false);
    }
    if (!selectedFoil && !foilAvailability.nonFoil && foilAvailability.foil) {
      setSelectedFoil(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPrintingId, foilAvailability.foil, foilAvailability.nonFoil]);

  // targetCardId is directly the selected printing
  const targetCardId = selectedPrintingId;

  // Get scryfallID for selected printing
  const displayScryfallId = useMemo(() => {
    if (!selectedPrintingId) return card?.card?.scryfallID;
    const selected = printingOptions.find(p => p.id === selectedPrintingId);
    return selected?.scryfallID || card?.card?.scryfallID;
  }, [printingOptions, selectedPrintingId, card?.card?.scryfallID]);

  // Get point value for selected printing
  const estimatedPrice = useMemo((): number | null => {
    if (!selectedPrintingId) return null;
    const selected = printingOptions.find(p => p.id === selectedPrintingId);
    if (!selected) return null;
    return selectedFoil ? selected.pointsFoil : selected.pointsNonFoil;
  }, [printingOptions, selectedPrintingId, selectedFoil]);

  const handleSave = async () => {
    if (!card || !targetCardId) {
      return;
    }

    setIsSaving(true);
    try {
      const updates: UpdateHaveListCardRequest = {
        haveListRecordId: card.id, // UserMTGCardHave record ID
        quantity,
        condition: selectedCondition,
        foil: selectedFoil,
        forTrade: selectedForTrade,
      };

      // Only include newCardId if we're changing the printing
      if (targetCardId !== card.card?.id) {
        updates.newCardId = targetCardId;
      }

      await onSave(updates);
      onClose();
    } catch (error) {
      console.error('Failed to update card:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen || !card) return null;

  const modalContent = (
    <div className="edit-modal-overlay" onClick={handleClose}>
      <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h2 className="edit-modal-title">Edit: {card.cardName || card.card?.name}</h2>
          <button className="edit-close-button" onClick={handleClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="edit-modal-content">
          {/* Card Image */}
          <div className="edit-card-image-container">
            {displayScryfallId && (
              <FoilCardImage
                src={AssetUrls.cards.getCardImage(displayScryfallId, 'normal')}
                alt={card.cardName || card.card?.name || 'Card'}
                className="edit-card-image"
                foil={selectedFoil}
                borderRadius="12px"
                onError={(e) => {
                  const img = e.currentTarget;
                  const currentSrc = img.src;

                  // First try backend-provided fallback URLs if available
                  const fallbacks = (card.card as any)?.normal_fallbacks;
                  if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
                    img.dataset.fallbackAttempted = 'true';
                    img.src = fallbacks[0];
                    return;
                  }

                  // Otherwise, try different image formats using scryfallId
                  if (displayScryfallId && !img.dataset.formatFallbackComplete) {
                    // Determine current format from URL
                    if (currentSrc.includes('/normal/')) {
                      // Normal format failed, try small
                      img.src = AssetUrls.cards.getCardImage(displayScryfallId, 'small');
                      return;
                    } else if (currentSrc.includes('/small/')) {
                      // Small format failed, try large
                      img.src = AssetUrls.cards.getCardImage(displayScryfallId, 'large');
                      return;
                    } else if (currentSrc.includes('/large/')) {
                      // Large format failed, mark as complete and use placeholder
                      img.dataset.formatFallbackComplete = 'true';
                    }
                  }

                  // If all fallbacks failed, use default card back
                  img.src = AssetUrls.cards.defaultBack;
                }}
              />
            )}
          </div>

          {/* Quantity */}
          <div className="edit-form-group">
            <label className="edit-label">Quantity</label>
            <div className="edit-quantity-controls">
              <button
                className="edit-quantity-btn"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
              >
                <FontAwesomeIcon icon="minus" />
              </button>
              <input
                type="number"
                className="edit-quantity-input"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                min={1}
              />
              <button
                className="edit-quantity-btn"
                onClick={() => setQuantity(quantity + 1)}
              >
                <FontAwesomeIcon icon="plus" />
              </button>
            </div>
          </div>

          {/* Printing Selection */}
          <div className="edit-form-group">
            <label className="edit-label">Printing</label>
            {isPrintingsLoading ? (
              <div className="edit-loading">Loading printings...</div>
            ) : (
              <>
                <input
                  type="text"
                  className="edit-input"
                  placeholder="Search by set name, code, or collector #..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select
                  className="edit-select"
                  value={selectedPrintingId ?? ''}
                  onChange={(e) => setSelectedPrintingId(Number(e.target.value) || null)}
                >
                  <option value="">Select a printing...</option>
                  {filteredPrintings.map(p => {
                    const points = p.pointsNonFoil ?? p.pointsFoil;
                    const pointsStr = points !== null ? `${points.toLocaleString()} pts` : '???';
                    return (
                      <option key={p.id} value={p.id}>
                        {p.setName} ({p.setCode.toUpperCase()}) #{p.collectorNumber} - {pointsStr}
                      </option>
                    );
                  })}
                </select>
              </>
            )}
          </div>

          {/* Foil */}
          <div className="edit-form-group">
            <label className="edit-checkbox-label">
              <input
                type="checkbox"
                checked={selectedFoil}
                onChange={(e) => setSelectedFoil(e.target.checked)}
                disabled={!foilAvailability.foil}
              />
              <span>Foil version</span>
              {!foilAvailability.foil && <span className="edit-unavailable">(Not available)</span>}
            </label>
          </div>

          {/* For Trade */}
          <div className="edit-form-group">
            <div className="edit-for-trade-row">
              <label className="edit-label">For Trade</label>
              <ToggleSwitch
                checked={selectedForTrade}
                onChange={setSelectedForTrade}
              />
            </div>
            <span className="edit-for-trade-hint">
              {selectedForTrade ? 'This card will appear in auto-match results' : 'This card will not be available for trading'}
            </span>
          </div>

          {/* Condition */}
          <div className="edit-form-group">
            <label className="edit-label">Condition</label>
            <select
              className="edit-select"
              value={selectedCondition}
              onChange={(e) => setSelectedCondition(e.target.value as CardCondition)}
            >
              {CONDITION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Language */}
          <div className="edit-form-group">
            <label className="edit-label">Language</label>
            <select
              className="edit-select"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
            >
              {availableLanguages.length > 0 ? (
                availableLanguages.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))
              ) : (
                <option value={selectedLanguage}>No languages available</option>
              )}
            </select>
          </div>

          {/* Price Preview */}
          <div className="edit-price-section">
            <div className="edit-price-label">Value:</div>
            <div className="edit-price-value">
              {estimatedPrice !== null
                ? `${estimatedPrice.toLocaleString()} pts`
                : 'Price unavailable'}
            </div>
          </div>

          {/* Validation Warning */}
          {!targetCardId && selectedPrintingId !== null && (
            <div className="edit-warning">
              This combination is not available for this card
            </div>
          )}
        </div>

        <div className="edit-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={handleClose}
            isDisabled={isSaving}
            fullWidth={false}
          />
          <MagicButton
            label={isSaving ? "Saving..." : "Save Changes"}
            color="purple"
            onPress={handleSave}
            isDisabled={!targetCardId || isSaving}
            isLoading={isSaving}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EditHaveListCardModal;
